import { useQuery } from '@tanstack/react-query';
import {
  CreditAccountsFetchStatus as CreditAccountsFetchStatus,
  useAppState,
  useBankSearchState,
  useCreditAccountsState
} from './zustandHooks';
import {
  accessTokenStorage,
  bankSelectApi,
  configurationService,
  configurationStorageService,
  connectApi,
  localStorageContainer,
  providerService,
  userIdStorage
} from '../domain/services';
import {
  consentConfigurationFn,
  consentFn,
  consentInfoFn,
  paymentAccountConfirmationSummaryFn,
  connectionSummaryFn
} from './utils';
import { useDebounce } from './useDebounce';
import { trackEvent } from '../App';
import { EventTypes } from '../utils/applicationInsights';
import { CIS_ACCOUNT_BEARER } from '../constants/constants';
import { useEffect } from 'react';

export const queryKeys = {
  settings: 'settings',
  serviceUrls: 'service_urls',
  countriesList: 'countries_list',
  banks: 'banks',
  favouriteBanks: 'favourite_banks',
  consentConfiguration: 'consent_configuration',
  consent: 'consent',
  connectionSummary: 'connection_summary',
  consentInfo: 'consent_info',
  providerPreSelection: 'provider_pre_selection',
  bankCodePreSelect: 'bank_code_pre_select',
  paymentAccountConfirmationSummary: 'payment_account_confirmation_summary',
  creditDataAccounts: 'credit_data_accounts',
  tuBankSearch: 'tu_bank_search',
  kbaQuestions: 'kba_questions',
  loqateFind: 'loqate_find',
  loqateRetrieve: 'loqate_retrieve'
};

export const useSettings = () => {
  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);
  const rpName = useAppState(state => state.appState.rpName);
  const disableCreditInformationFlow = useAppState(state => state.appState.disableCreditInformationFlow);

  const setAppState = useAppState(state => state.setAppState);

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.settings],
    enabled: !!clientId,
    queryFn: async () => {
      const data = await configurationService.getSettings(clientId, configurationId);
      if (data && !configurationStorageService.hasItem(clientId)) {
        configurationStorageService.setItem(clientId, JSON.stringify(data));
      }

      if (!rpName && data?.customerName) setAppState({ rpName: data?.customerName });

      if (disableCreditInformationFlow) data.enableCreditInformationFlow = false;

      return data;
    }
  });

  return {
    settings: data,
    isSettingsLoading: isLoading,
    isSettingsError: isError,
    ...other
  };
};

export const useServiceUrls = () => {
  const clientId = useAppState(state => state.appState.clientId);
  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.serviceUrls],
    enabled: !!clientId,
    queryFn: async () => await configurationService.getDiscoveryUrls(clientId)
  });

  return {
    serviceUrls: data!,
    isServiceUrlsLoading: isLoading,
    isServiceUrlsError: isError,
    ...other
  };
};

export const useCountriesList = () => {
  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);
  const setSelectedCountry = useBankSearchState(state => state.setSelectedCountry);
  const { serviceUrls } = useServiceUrls();

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.countriesList],
    enabled: !!serviceUrls?.providerSearchService,
    meta: {
      error_code: 'load_countries_failed'
    },
    queryFn: async ({ signal }) => {
      const data = await providerService.getCountryCodesList(
        serviceUrls?.providerSearchService!,
        clientId,
        configurationId,
        signal
      );
      setSelectedCountry(data.defaultCountry);

      return {
        selectedCountry: data?.defaultCountry || 'GB',
        countryCodes: data?.countries.map(c => c.code) || []
      };
    }
  });

  return {
    countriesList: data,
    isCountriesListLoading: isLoading,
    isCountriesListError: isError,
    ...other
  };
};

export const useBanks = (query?: string) => {
  const { serviceUrls } = useServiceUrls();
  const { selectedCountry } = useBankSearchState();

  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.banks, selectedCountry, query],
    enabled: !!query && !!serviceUrls?.providerSearchService,
    queryFn: async ({ signal }) => {
      window.hj?.('tagRecording', ['Searched']);
      const response = await bankSelectApi.getBanks(
        serviceUrls?.providerSearchService!,
        clientId,
        configurationId,
        { query },
        selectedCountry,
        signal
      );

      if (window.hj && query) {
        if (response?.length) {
          window.hj('tagRecording', ['Search Results Listed']);
        } else if (response?.length === 0) {
          window.hj('tagRecording', ['Search Results None']);
        }
      }

      return response;
    }
  });

  return {
    banks: data,
    isBanksLoading: isLoading,
    isBanksError: isError,
    ...other
  };
};

export const useFavouriteBanks = () => {
  const { isCountriesListLoading } = useCountriesList();
  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);
  const { serviceUrls } = useServiceUrls();
  const selectedCountry = useBankSearchState(state => state.selectedCountry);

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.favouriteBanks, selectedCountry],
    enabled: !!serviceUrls?.providerSearchService && !isCountriesListLoading,
    queryFn: async ({ signal }) => {
      return await bankSelectApi.getFavouriteBanks(
        serviceUrls?.providerSearchService!,
        clientId,
        configurationId,
        selectedCountry,
        signal
      );
    }
  });

  return {
    favouriteBanks: data,
    isFavouriteBanksLoading: isLoading,
    isFavouriteBanksError: isError,
    ...other
  };
};

export const useBanksList = (search?: string) => {
  const { isBanksLoading, isBanksError, banks } = useBanks(search);
  const { favouriteBanks, isFavouriteBanksError, isFavouriteBanksLoading } = useFavouriteBanks();

  return {
    isLoading: isBanksLoading || isFavouriteBanksLoading,
    data: banks || favouriteBanks,
    banks,
    favouriteBanks,
    isError: isBanksError || isFavouriteBanksError
  };
};

export const useLoqateFind = (query: { search: string; containerId?: string }) => {
  const { serviceUrls } = useServiceUrls();
  const debouncedSearch = useDebounce(query.search, 500);
  return useQuery({
    queryKey: [queryKeys.loqateFind, debouncedSearch],
    enabled: !!debouncedSearch,
    queryFn: async () =>
      await connectApi.loqateFind(serviceUrls?.connectApiService!, debouncedSearch, query?.containerId)
  });
};
export const useLoqateRetrieve = (addressId: string) => {
  const { serviceUrls } = useServiceUrls();
  return useQuery({
    queryKey: [queryKeys.loqateRetrieve, addressId],
    enabled: !!addressId,
    queryFn: async () => await connectApi.loqateRetrieve(serviceUrls?.connectApiService!, addressId)
  });
};

export const useConsentConfiguration = () => {
  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);
  const language = useAppState(state => state.appState.language);

  const { consent } = useConsent();

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.consentConfiguration, clientId, language, configurationId],
    queryFn: async () => await consentConfigurationFn(consent)
  });

  return {
    consentConfiguration: data,
    isConsentConfigurationLoading: isLoading,
    isConsentConfigurationError: isError,
    ...other
  };
};

export const useConsent = () => {
  const consentId = useAppState(state => state.appState.consentId);
  const reAuthentication = useAppState(state => state.appState.reAuthentication);
  const isReconfirmation = useAppState(state => state.appState.isReconfirmation);

  const { serviceUrls } = useServiceUrls();

  const { data, isLoading, isError, ...other } = useQuery({
    enabled: !!consentId && !!serviceUrls?.consentService && (isReconfirmation || reAuthentication),
    queryKey: [queryKeys.consent],
    queryFn: async () => await consentFn(serviceUrls)
  });

  return {
    consent: data?.consent,
    bank: data?.bank,
    isConsentLoading: isLoading,
    isConsentError: isError,
    ...other
  };
};

export const useConnectionSummary = (isEnabled = true) => {
  const { serviceUrls } = useServiceUrls();
  const reAuthentication = useAppState(state => state.appState.reAuthentication);
  const isReconfirmation = useAppState(state => state.appState.isReconfirmation);

  const token = accessTokenStorage.getToken() as string; // in redirect url

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.connectionSummary],
    enabled: !!serviceUrls?.gatewayService && !reAuthentication && !!token && !isReconfirmation && isEnabled,
    queryFn: async () => await connectionSummaryFn(serviceUrls?.gatewayService!, token)
  });

  return {
    connectionSummary: data,
    isConnectionSummaryLoading: isLoading,
    isConnectionSummaryError: isError,
    ...other
  };
};

export const useCreditDataAccounts = (enableCisQuery?: boolean) => {
  const { serviceUrls } = useServiceUrls();
  const creditAccountsFetchStatus = useCreditAccountsState(zState => zState.state.creditAccountsFetchStatus);
  const cisToken = localStorageContainer.getTokenByClientId(CIS_ACCOUNT_BEARER);
  const enableQuery =
    cisToken != null && (enableCisQuery || creditAccountsFetchStatus === CreditAccountsFetchStatus.Finished);

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.creditDataAccounts],
    enabled: enableQuery,
    queryFn: async () => {
      return await connectApi.getCreditDataAccounts(serviceUrls?.connectApiService);
    }
  });

  const hasFetchingError = isError || creditAccountsFetchStatus === CreditAccountsFetchStatus.Error;
  useEffect(() => {
    if (hasFetchingError) {
      trackEvent({
        type: EventTypes.CREDIT_DATA_ACCOUNTS_ERROR,
        payload: {
          creditAccountsFetchStatus: creditAccountsFetchStatus,
          isCisTokenNull: cisToken == null,
          error: JSON.stringify(other.error)
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditAccountsFetchStatus, hasFetchingError]);

  return {
    creditData: data,
    isCreditDataLoading: isLoading,
    isCreditDataError: hasFetchingError || cisToken == null || localStorageContainer.isTokenExpired(cisToken),
    ...other
  };
};

export const useConsentInfo = (enabled: boolean = true) => {
  const { serviceUrls } = useServiceUrls();

  const { data, isLoading, isError, ...other } = useQuery({
    queryKey: [queryKeys.consentInfo],
    enabled: !!serviceUrls?.gatewayService && enabled,
    queryFn: async () => await consentInfoFn(serviceUrls)
  });

  return {
    consentInfo: data,
    isConsentInfoLoading: isLoading,
    isConsentInfoError: isError,
    ...other
  };
};

export const usePaymentAccountConfirmationSummary = () => {
  const { serviceUrls } = useServiceUrls();
  // const token = accessTokenStorage.getToken();
  // const reAuthentication = useAppState(state => state.appState.reAuthentication);
  // const isReconfirmation = useAppState(state => state.appState.isReconfirmation);

  const { data, isLoading, ...other } = useQuery({
    queryKey: [queryKeys.paymentAccountConfirmationSummary],
    // enabled: !!serviceUrls?.gatewayService && !!token && !reAuthentication && !isReconfirmation,
    enabled: false,
    queryFn: async () => await paymentAccountConfirmationSummaryFn(serviceUrls)
  });

  return {
    paymentAccountConfirmationSummary: data,
    isPaymentAccountConfirmationLoading: isLoading,
    ...other
  };
};

export const useGetKbaQuestions = () => {
  const { serviceUrls } = useServiceUrls();
  const clientId = useAppState(state => state.appState.clientId);
  const tuUserId = useAppState(state => state.appState.tuUserId) as string;
  const userId = userIdStorage.getUserId() as string;
  const url = serviceUrls?.connectApiService as string;

  const { data, isFetching, isError, ...other } = useQuery({
    queryKey: [queryKeys.kbaQuestions],
    queryFn: async () => {
      trackEvent({ type: EventTypes.CIS_KBA_FETCH });
      return await connectApi.getKbaQuestions(url, userId, tuUserId, clientId);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000
  });

  return {
    questions: data?.questions,
    isFetching,
    isError,
    ...other
  };
};
